import React, { useState } from 'react';
import { Button, Spinner, Typography } from '@material-tailwind/react';
import {
  ArrowLeftIcon, EyeIcon, LayersIcon, LayoutTemplateIcon, PencilIcon, PlusIcon,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { BuildingStack } from '../../components/floor-plan';
import { canEditFloorPlan } from '../../components/floor-plan/constants';
import { useGetBuildingById } from '../../custom-hooks/use-buildings';
import { useGetBranchById } from '../../custom-hooks/use-branches';
import { useGetBuildingFloorStack } from '../../custom-hooks/use-floor-layouts';
import { useAuth } from '../../services/contexts/authContext';
import ListModeMenu from './list-mode-menu';
import FloorCrudModal from './floor-crud-modal';
import {
  managementBranchUrl,
  managementFloorEditUrl,
  managementFloorUrl,
} from './urls';

const ManagementBuildingFloorsPage = () => {
  const { branchId, buildingId } = useParams();
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const canEdit = canEditFloorPlan(userDetails?.role);

  const [selectedFloor, setSelectedFloor] = useState(null);
  const [floorModalOpen, setFloorModalOpen] = useState(false);
  const [editingFloor, setEditingFloor] = useState(null);

  const { data: branchRes } = useGetBranchById(branchId);
  const branch = branchRes?.data?.branch;
  const branchLabel = branch?.branchName || branch?.name || 'Branch';

  const { data: buildingRes, isLoading: buildingLoading } = useGetBuildingById(buildingId);
  const building = buildingRes?.data?.building;

  const { data: stackData, isLoading: stackLoading, refetch } = useGetBuildingFloorStack(buildingId);
  const floors = stackData?.floors || [];

  const handleSelectFloor = (floor) => {
    setSelectedFloor(floor);
  };

  const handleCreateLayout = (floor) => {
    navigate(managementFloorEditUrl(branchId, buildingId, floor._id));
  };

  const openAddFloor = () => {
    setEditingFloor(null);
    setFloorModalOpen(true);
  };

  const openManageFloor = () => {
    if (!selectedFloor) return;
    setEditingFloor(selectedFloor);
    setFloorModalOpen(true);
  };

  const viewFloorPlan = () => {
    if (!selectedFloor) return;
    navigate(managementFloorUrl(branchId, buildingId, selectedFloor._id));
  };

  const handleBack = () => {
    navigate(managementBranchUrl(branchId));
  };

  if (buildingLoading) {
    return (
      <div className="m-5 p-5 border rounded-3xl flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="m-5 p-5 border rounded-3xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <Button
            variant="text"
            className="rounded-full flex items-center gap-2 w-fit mb-2 -ml-2"
            onClick={handleBack}
          >
            <ArrowLeftIcon size={16} />
            Back to buildings
          </Button>
          <Typography variant="small" className="text-orange-600 font-medium uppercase tracking-wide text-[11px]">
            Branch · {branchLabel}
          </Typography>
          <h1 className="md:text-2xl font-thin">{building?.name || 'Building'}</h1>
          <Typography variant="small" className="text-gray-500 mt-1">
            {building?.buildingCode} · Select a floor to view or manage its plan
          </Typography>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListModeMenu branchId={branchId} branchName={branchLabel} buildingId={buildingId} />
          <Button
            size="sm"
            className="rounded-full flex items-center gap-2 normal-case font-normal"
            onClick={openAddFloor}
          >
            <PlusIcon size={14} />
            Add floor
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <BuildingStack
            floors={floors}
            building={building}
            isLoading={stackLoading}
            onSelectFloor={handleSelectFloor}
            onCreateLayout={handleCreateLayout}
            selectedFloorId={selectedFloor?._id}
          />
        </div>

        {selectedFloor && (
          <div className="w-full lg:w-80 shrink-0 rounded-2xl border bg-white p-4 h-fit lg:sticky lg:top-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <Typography variant="h6" className="truncate">
                  {selectedFloor.floorCode || `Floor ${selectedFloor.number}`}
                </Typography>
                <Typography variant="small" className="text-gray-500 capitalize">
                  {selectedFloor.status}
                </Typography>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 text-sm"
                onClick={() => setSelectedFloor(null)}
              >
                Close
              </button>
            </div>
            <Typography variant="small" className="text-gray-500 mb-3">
              {selectedFloor.occupiedSeats || 0}/{selectedFloor.totalSeats || 0} seats · {(selectedFloor.occupancyRate || 0).toFixed(0)}% occupied
            </Typography>
            {!selectedFloor.hasLayout && (
              <div className="rounded-xl bg-orange-50 border border-orange-100 px-3 py-2 text-xs text-orange-800 mb-3 flex items-center gap-2">
                <LayoutTemplateIcon size={14} />
                No published layout yet
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="outlined"
                className="rounded-full flex items-center justify-center gap-2 normal-case"
                onClick={openManageFloor}
              >
                <PencilIcon size={14} />
                Edit floor
              </Button>
              {selectedFloor.hasLayout ? (
                <Button
                  size="sm"
                  className="rounded-full flex items-center justify-center gap-2 normal-case"
                  onClick={viewFloorPlan}
                >
                  <EyeIcon size={14} />
                  View floor plan
                </Button>
              ) : canEdit ? (
                <Button
                  size="sm"
                  className="rounded-full flex items-center justify-center gap-2 normal-case"
                  onClick={() => handleCreateLayout(selectedFloor)}
                >
                  <LayersIcon size={14} />
                  Create layout
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {!selectedFloor && stackData?.totals && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Floors', value: stackData.totals.floorCount },
            { label: 'Total seats', value: stackData.totals.totalSeats },
            { label: 'Occupied', value: stackData.totals.occupiedSeats },
            { label: 'Occupancy', value: `${(stackData.totals.occupancyRate || 0).toFixed(1)}%` },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl bg-gray-50 p-3">
              <Typography variant="small" className="text-gray-500">{stat.label}</Typography>
              <Typography variant="h6">{stat.value}</Typography>
            </div>
          ))}
        </div>
      )}

      <FloorCrudModal
        isOpen={floorModalOpen}
        onClose={() => {
          setFloorModalOpen(false);
          setEditingFloor(null);
          refetch();
        }}
        buildingId={buildingId}
        floor={editingFloor}
        existingCount={floors.length}
      />
    </div>
  );
};

export default ManagementBuildingFloorsPage;
